import { Component, OnChanges, OnInit, SimpleChanges, forwardRef } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { AbstractFormFieldComponent } from '../../../../PageBuilder/components/FormField/abstract-form-field.component';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { ChartDisplayElementModel } from '../../../models/chart-display-element.model';
import { Mention } from '../../../../Mentions/models/mention';
import { ReportImportMentionDialogComponent } from '../../ReportImportMentionDialog/report-import-mention-dialog.component';
import { SearchCriteriaSortFieldEnum } from '../../../../../api/mentions/enum/search-criteria-sort-field.enum';

import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { ReportBlockTypeEnum } from '../../../enums/report-block-type.enum';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';

@Component({
    selector: 'agpr-top-curated-mentions-field',
    templateUrl: './top-curated-mentions-field.component.html',
    styleUrls: ['./top-curated-mentions-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => TopCuratedMentionsFieldComponent)
        }
    ],
    standalone: false
})
export class TopCuratedMentionsFieldComponent extends AbstractFormFieldComponent implements OnInit, OnChanges {

    id: string = 'topCuratedMentions';
    showCuratedMentions: boolean = true;
    mentions: Mention[] = [];
    maxSelection: number = 5;
    importMentionDialogKey: string;
    onlySocial: boolean = false;

    header: string = 'Reports.topMentions.topCuratedMentions.header';
    tooltip: string = 'Reports.topMentions.topCuratedMentions.header.icon.message';
    maxSelectionTooltip: string = 'Reports.topMentions.topCuratedMentions.header.icon.warning';

    draggingIndex: number = null;

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    constructor(public coreDialogService: CoreDialogService) {
        super();
    }

    ngOnInit(): void {
        this.mentions = this.value?.slice();

        if (this.block.type.key === ReportBlockTypeEnum.TOP_POSTS) {
            this.header = 'Reports.topPosts.topCuratedMentions.header';
            this.tooltip = 'Reports.topPosts.topCuratedMentions.header.icon.message';
            this.maxSelectionTooltip = 'Reports.topPosts.topCuratedMentions.header.icon.warning';
            this.onlySocial = true;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((this.block.displayElement as ChartDisplayElementModel).numOfItems) {
            this.maxSelection = (this.block.displayElement as ChartDisplayElementModel).numOfItems;
        } else {
            this.maxSelection = 5;
        }
        this.showCuratedMentions = (this.block.displayElement as ChartDisplayElementModel).topMentionsSortField === SearchCriteriaSortFieldEnum.custom;
    }

    open(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer,
                reportMentions: this.mentions,
                maxSelection: this.maxSelection,
                onlySocial: this.onlySocial
            },
            outputs: {
                mentionsAdded: (mentions: Mention[]) => {
                    const mentionIds = this.mentions.map(x => x.mentionId);
                    mentions.forEach(mention => {
                        if (mentionIds.includes(mention.mentionId)) {
                            return;
                        }
                        this.mentions.push(mention);
                        this.updateMentions();
                        // this is needed to refresh the added mentions count and badges
                        const dialog = this.coreDialogService.getDialog(this.importMentionDialogKey)?.instance;
                        if (dialog) {
                            dialog['reportMentions'] = this.mentions;
                        }
                    });
                },
                dialogClose: () => {
                    this.importMentionDialogKey = null;
                }
            }
        };
        if (!this.importMentionDialogKey) {
            this.importMentionDialogKey = this.coreDialogService.open(ReportImportMentionDialogComponent, ioModel);
        }
    }

    moveUp(index: number): void {
        const mention = this.mentions[index].clone();
        this.mentions.splice(index, 1);
        this.mentions.splice(index - 1, 0, mention);
        this.updateMentions();
    }

    moveDown(index: number): void {
        const mention = this.mentions[index].clone();
        this.mentions.splice(index, 1);
        this.mentions.splice(index + 1, 0, mention);
        this.updateMentions();
    }

    delete(index: number): void {
        this.mentions.splice(index, 1);
        this.updateMentions();
    }

    updateMentions(): void {
        (this.block.displayElement as ChartDisplayElementModel).series[0].mentionProvider.next(this.mentions);
        this.value = this.mentions.slice();
        this.changes.emit(this.value);
    }

    onDrop(index) {
        const mention = this.mentions.splice(this.draggingIndex, 1)[0];
        this.mentions.splice(index, 0, mention);
        this.updateMentions();
    }

    onDragStart(index) {
        this.draggingIndex = index;
    }
}
